import React, { useMemo } from 'react';
import { Box, Button, Typography, Stack, Chip } from '@mui/material';
import CollapsibleTable from '../../components/table';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import SnackbarAlert from '../../components/SnackbarAlert';
import ModalTesorero from '../../components/ModalTesorero';
import FileActionModal from '../../components/FileActionModal';
import ExportButton from '../../components/BotonEx';
import FilterComponent from '../../components/FilterComponent';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

import { useTesoreroController } from './hooks/useTesoreroController';
import ModalSolicitarFondos from '../../components/ModalSolicitarFondos';
import ModalPagoMasivo from '../../components/ModalPagoMasivo/ModalPagoMasivo';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';

export default function Tesorero() {
  const {
    data, loading, loadingModal, loadingModalMessage, setLoadingModal,
    fileModalOpen, setFileModalOpen, fileList, initialFileId,

    modalStates, setModalStates, snackbar, setSnackbar,
    empresaIds, empresaIdPref, usuarioId, esTesorero, hiddenFieldsMemo,
    selectedRows, setSelectedRows, modalFondosOpen, setModalFondosOpen,
    modalPagoMasivoOpen, setModalPagoMasivoOpen,
    tempFilterData, setTempFilterData, ejecutarBusquedaManual, tableParams,
    esTesoreroAsignado, fetchData, handleTableStateChange, handleExportFetchAll,
    cancelarSolicitud,
    correoConfirmarPrecio, handleViewFile, handleViewSpecificFile, openSolicitarFondosModal, openPagoMasivoModal, apiUrl
  } = useTesoreroController();

  const [rowParaPago, setRowParaPago] = React.useState(null);
  const [modalRegistroPagoOpen, setModalRegistroPagoOpen] = React.useState(false);

  const totalSolicitado = useMemo(() => {
    return selectedRows.reduce((sum, r) => {
      const raw = r.MONTOSOLICITADO || r.MONTO_SOLICITADO || 0;
      const num = typeof raw === 'string' ? Number(raw.replace(/[^0-9.-]+/g, "")) : Number(raw);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);
  }, [selectedRows]);

  const formatCurrency = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val);

  const handleProcesarPago = (row) => {
    if (!esTesoreroAsignado(row)) {
      handleOpenSweetAlert('Solo el tesorero asignado puede procesar el pago.', 'error');
      return;
    }
    if (Number(row.CONFIRMARPRECIO) !== 1) {
      handleOpenSweetAlert('Confirme el precio antes de procesar el pago.', 'info');
      return;
    }
    setRowParaPago(row);
    setModalRegistroPagoOpen(true);
  };

  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Subir Comprobante (Pagar)',
      onClick: handleProcesarPago,
      icon: <AttachFileIcon />,
      buttonColor: '#59C12C',
      buttonFontColor: '#ffffff',
      isVisible: true,
    },
    {
      id: 4,
      label: 'Ver OC',
      onClick: (row) => handleViewSpecificFile(row, row.ID_OC),
      icon: <AssignmentTurnedInIcon />,
      buttonColor: '#ed6c02',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !row.HAS_OC,
    },
    {
      id: 3,
      label: 'Ver Carátula',
      onClick: (row) => handleViewSpecificFile(row, row.ID_CB),
      icon: <AccountBalanceIcon />,
      buttonColor: '#0288d1',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !row.HAS_CB,
    },
    {
      id: 2,
      label: 'Ver Factura',
      onClick: (row) => handleViewSpecificFile(row, row.ID_FACTURA),
      icon: <ReceiptIcon />,
      buttonColor: '#2e7d32',
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !row.HAS_FACTURA,
    },
    {
      id: 5,
      label: 'Visualizar Archivos',
      onClick: handleViewFile,
      icon: <FolderIcon />,
    },
  ], [handleViewFile, handleViewSpecificFile, esTesoreroAsignado, esTesorero]);

  const formattedData = useMemo(() => {
    const rawList = Array.isArray(data?.contenido) ? data.contenido : (Array.isArray(data) ? data : []);
    const mapped = rawList.map((row) => ({
      ...row,
      TESORERO_ID_FOLIO_EMPRESA: '',
      PROVEEDOR_COMPRADOR: '',
      CENTRO_DEPTO: '',
      TOTAL_DIVISA: '',
      MONTO_CONFIRMADO_PRECIO: '',
      NOTAS_PARTIDAS_ECO: '',
      NOTAS_REQUISICION: '',
      TESORERO1: row.TESORERO1 || row.TESORERO || '',
    }));
    return Array.isArray(data) ? mapped : { ...data, contenido: mapped };
  }, [data]);

  const compactColumnOrderMemo = useMemo(() => [
    'TESORERO_ID_FOLIO_EMPRESA',
    'PROVEEDOR_COMPRADOR',
    'CENTRO_DEPTO',
    'TOTAL_DIVISA',
    'MONTO_CONFIRMADO_PRECIO',
    'NOTAS_PARTIDAS_ECO',
    'NOTAS_REQUISICION',
    'TESORERO1',
  ], []);

  const compactColumnLabelsMemo = useMemo(() => ({
    TESORERO_ID_FOLIO_EMPRESA: 'Orden / ID',
    PROVEEDOR_COMPRADOR: 'Proveedor / Comprador',
    CENTRO_DEPTO: 'Centro / Departamento',
    TOTAL_DIVISA: 'Total / Divisa',
    MONTO_CONFIRMADO_PRECIO: 'Monto / Precio',
    NOTAS_PARTIDAS_ECO: 'Notas / No. Eco',
    NOTAS_REQUISICION: 'Requisición',
    TESORERO1: 'Tesorero',
  }), []);

  const hiddenFieldsMemoCustom = useMemo(() => [
    // Campos primitivos/duplicados ocultos visualmente (conservando los datos internos intactos)
    'ID',
    'ID_SOLICITUD',
    'FOLIO',
    'FOLIO_OC',
    'MONTO',
    'TOTAL',
    'TOTALOC',
    'DIVISA',
    'MONTOSOLICITADO',
    'MONTO_SOLICITADO',
    'CONFIRMARPRECIO',
    'RFC',
    'NOMBRE_EMPRESA',
    'EMPRESA',
    'EMPRESAID',
    'CENTROCOSTO',
    'CENTROCOSTOID',
    'NUMEROCC',
    'NUMERO_CC',
    'DEPARTAMENTO',
    'DEPARTAMENTOID',
    'SOLICITANTE',
    'COMPRADOR',
    'PROVEEDOR',
    'NOTASPARTIDAS',
    'NOTAS_PARTIDAS',
    'NUMEROECONOMICO',
    'NUMERO_ECONOMICO',
    'NOTASREQUISICION',
    'NOTAS_REQUISICION',
    'METODOPAGO',
    'ESTATUS',
    'COMPROBANTE',
    'IMAGEN',
    'REFERENCIA',
    'HORA',
    // Ocultar las columnas desmarcadas indicadas en las capturas
    'VENCIMIENTO',
    'AUTNIV1',
    'AUTNIV2',
    'DIASRESTANTES',
    'DIAS_RESTANTES',
    'NOTASPARTIDA',
    'TESORERO',
    'HAS_FACTURA',
    'ID_FACTURA',
    'HAS_CB',
    'ID_CB',
    'HAS_OC',
    'ID_OC',
    'HAS_OTROS',
    'ID_OTROS',
    'cadenaAut',
    'MOTIVO_CORRECCION',
    'DETALLE_CORRECCION',
    'SECCIONES_CORREGIR',
    'REQUIERE_RECLASIFICACION'
  ], []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 'clamp(6px, 0.8vw, 12px)', gap: 'clamp(6px, 0.8vw, 10px)', overflow: 'hidden', bgcolor: '#f8fafc' }}>

      <SimpleSuccessSpinner open={loadingModal} handleClose={() => setLoadingModal(false)} message={loadingModalMessage} />
      <SnackbarAlert open={snackbar.open} message={snackbar.msg} severity={snackbar.sev} onClose={() => setSnackbar(p => ({ ...p, open: false }))} />

      {modalPagoMasivoOpen && (
        <ModalPagoMasivo
          open={modalPagoMasivoOpen}
          handleCloseModal={() => setModalPagoMasivoOpen(false)}
          selectedRows={selectedRows}
          onSaved={() => { setSelectedRows([]); fetchData(); }}
        />
      )}

      {selectedRows.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip 
              label={`Seleccionado: ${formatCurrency(totalSolicitado)} (${selectedRows.length} filas)`} 
              color="primary" 
              variant="outlined" 
              sx={{
                fontWeight: 800,
                fontSize: 'clamp(0.70rem, 0.65rem + 0.1vw, 0.82rem)',
                height: 'clamp(24px, 2vw, 30px)',
                bgcolor: 'rgba(6, 85, 165, 0.08)',
                color: '#0655A5',
                borderColor: 'rgba(6, 85, 165, 0.3)'
              }} 
            />
          </Stack>
        </Stack>
      )}

      <FilterComponent
        filterData={tempFilterData}
        setFilterData={setTempFilterData}
        handleClick={ejecutarBusquedaManual}
        initialFilters={['FECHA_INICIO', 'FECHA_FIN', 'FOLIO', 'EMPRESA']}
        showExportButton={false}
      />

      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          loading={loading}
          dataTable={formattedData}
          buttons={buttons}
          showExportButton={true}
          compactActions
          maxInlineActions={4}
          mobileCard={{ statusMode: 'none' }}
          rightButtons={<ExportButton data={data} onFetchAll={handleExportFetchAll} fileName="Tesorero.xlsx" hidePartidas hidePendientes />}
          initialColumnOrder={compactColumnOrderMemo}
          columnLabels={compactColumnLabelsMemo}
          hiddenFields={hiddenFieldsMemoCustom}
          stickyLeftKeys={['TESORERO_ID_FOLIO_EMPRESA']}
          density="small"
          persistEnabled
          persistKey="Tesorero"
          persistVista="layoutFitWidthV17"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
          selectable={true}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          onStateChange={handleTableStateChange}
          serverPagination={true}
          totalServerRows={data?.total || 0}
          selectionActions={
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button variant="contained" color="secondary" size="small" onClick={openSolicitarFondosModal} sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}>
                Solicitar Fondos
              </Button>
              <Button variant="contained" color="success" size="small" onClick={openPagoMasivoModal} sx={{ fontWeight: 'bold', borderRadius: 2, textTransform: 'none' }}>
                Carga Masiva
              </Button>
            </Box>
          }
          selectionSummary={
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', whiteSpace: 'nowrap' }}>
              SUMA: {formatCurrency(totalSolicitado)}
            </Typography>
          }
        />
      </Box>

      <FileActionModal open={fileModalOpen} onClose={() => setFileModalOpen(false)} files={fileList} initialFileId={initialFileId} />


      <ModalTesorero
        open={modalRegistroPagoOpen}
        handleCloseModal={() => { setModalRegistroPagoOpen(false); setRowParaPago(null); }}
        selectedRowData={rowParaPago}
        onSaved={fetchData}
      />



      <ModalSolicitarFondos
        open={modalFondosOpen}
        onClose={() => setModalFondosOpen(false)}
        selectedRows={selectedRows}
        empresaIds={empresaIds}
        onSent={() => { setSelectedRows([]); setModalFondosOpen(false); }}
      />
    </Box>
  );
}